// @flow

import React, { Component } from 'react';
import { compose } from 'recompose';
import { withRouter, type ContextRouter } from 'react-router-dom';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';

import fetch from 'node-fetch';
import uuid from 'uuid';

import {
  Input,
  PasswordInput,
  TextInput,
  injectSheet,
  type InjectSheetProvidedProps,
  type ThemeType
} from '@catalytic/catalytic-ui';

import { validate as validatePassword } from '@catalytic/pushbot-password-validator';
import {
  isReservedName,
  isAlphanumeric,
  isFirstLetter
} from './usernameValidators';

import LoginButton from '../shared/Button/LoginButton';
import LoginHeadline from './LoginHeadline';
import LoginSplitLayout from './LoginSplitLayout';
import { getReferrer } from './Login';
import withTeam, { type WithTeamProps } from './withTeam';

const EMAIL_KEY = 'email';
const FIRST_NAME_KEY = 'firstName';
const LAST_NAME_KEY = 'lastName';
const NICKNAME_KEY = 'nickname';
const PASSWORD_KEY = 'password';
const HIDE_NICKNAME = true;

export const Messages = defineMessages({
  firstNamePlaceholder: {
    id: 'login.joinFirstName',
    defaultMessage: 'first name'
  },
  joinError: {
    id: 'join.joinError',
    defaultMessage: 'Error joining team:'
  },
  lastNamePlaceholder: {
    id: 'login.joinLastName',
    defaultMessage: 'last name'
  },
  nicknameFirstLetterError: {
    id: 'join.nicknameLengthError',
    defaultMessage: 'Nicknames must begin with a lowercase letter.'
  },
  nicknameLengthError: {
    id: 'join.nicknameLengthError',
    defaultMessage: 'Your nickname must be longer than one letter.'
  },
  nicknameNonAlphaNumericError: {
    id: 'join.nicknameLengthError',
    defaultMessage:
      'Nicknames may only contain lowercase alphanumeric characters without spaces.'
  },
  nicknamePlaceholder: {
    id: 'login.joinNickname',
    defaultMessage: 'nickname'
  },
  passwordCatalyticError: {
    id: 'join.passwordCatalyticError',
    defaultMessage: 'You should not use "Catalytic" in your password'
  },
  passwordComplexityError: {
    id: 'join.passwordComplexityError',
    defaultMessage: 'Your password is not strong enough'
  },
  passwordEmailError: {
    id: 'join.passwordEmailError',
    defaultMessage: 'You should not use your email address in your password'
  },
  passwordLengthError: {
    id: 'join.passwordLengthError',
    defaultMessage: 'Passwords must be 8 characters or longer'
  },
  passwordNameError: {
    id: 'join.passwordNameError',
    defaultMessage: 'You should not use your name in your password'
  },
  passwordNicknameError: {
    id: 'join.passwordNicknameError',
    defaultMessage: 'You should not use your nickname in your password'
  },
  passwordPlaceholder: {
    id: 'login.joinPassword',
    defaultMessage: 'password'
  },
  passwordPushbotError: {
    id: 'join.passwordPushbotError',
    defaultMessage: 'You should not use "Workflow" in your password'
  },
  passwordTeamError: {
    id: 'join.passwordTeamError',
    defaultMessage: 'You should not use your team name in your password'
  },
  emailErrorMessage: {
    id: 'join.emailErrorMessage',
    defaultMessage: `The email "{email}" is already being used.`
  }
});

export const Style = (theme: ThemeType) => ({
  form: {
    margin: 'auto',
    padding: '1rem',
    minHeight: '1rem',
    maxWidth: '100%',
    width: '100vw',
    [theme.breakpoints.gt.mobile.portrait]: {
      width: '24rem'
    }
  },
  inputRoot: {
    marginBottom: '0.75rem'
  },
  input: { width: '100%', margin: 'auto' }
});

type Props = ContextRouter &
  InjectSheetProvidedProps &
  WithTeamProps & { intl: Object };

type State = {
  authed: boolean,
  disabled: boolean,
  email: string,
  emailError: string,
  error: string,
  firstName: string,
  firstNameError: string,
  lastName: string,
  lastNameError: string,
  password: string,
  passwordError: string,
  nickname: string,
  nicknameError: string,
  token: string
};
export class Join extends Component<Props, State> {
  static displayName = 'Join';

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { authed } = this.state;
    const { history, location } = this.props;
    const query = location.search;
    const searchParams = (new URLSearchParams(query): URLSearchParams);
    const token = searchParams.get('invitation');
    const referrer = getReferrer();
    const teamHostName = window.location.origin;
    const queryString = '?initialLocation=' + referrer;
    const pathname = `${teamHostName}/_/teams/${nextProps.teamName}/saml/login${queryString}`;

    let ssoUser = false;
    if (!authed && nextProps.teamName) {
      const INVALID_TOKEN_PATH = '/auth/join/invalid';

      if (token) {
        const INVITE_PATH = '/api/invites/' + token;
        const AUTHINFO_PATH = `/api/authinfo`;

        this.setState({ token: token });
        return fetch(AUTHINFO_PATH)
          .then(res => res.json())
          .then(res => {
            if (res.method === 'saml') {
              this.setState({ disabled: true, authed: true });
              ssoUser = true;
            }
            return fetch(INVITE_PATH);
          })
          .then(res => res.json())
          .then(res => {
            if (res.email) {
              this.setState({ email: res.email });
            } else {
              if (ssoUser) {
                return (window.location = pathname);
              } else {
                history.push({ pathname: INVALID_TOKEN_PATH });
              }
            }
          })
          .catch(e => {
            console.error(e);
          });
      } else {
        history.push({ pathname: INVALID_TOKEN_PATH });
      }
    }
  }

  state = {
    authed: false,
    disabled: false,
    email: '',
    emailError: '',
    error: '',
    firstName: '',
    firstNameError: '',
    lastName: '',
    lastNameError: '',
    password: '',
    passwordError: '',
    nickname: '',
    nicknameError: '',
    token: ''
  };

  handleChange = (event: SyntheticEvent<HTMLInputElement>) => {
    const {
      email,
      emailError,
      error,
      firstName,
      firstNameError,
      lastName,
      lastNameError,
      nickname,
      nicknameError,
      passwordError
    } = this.state;

    const { intl, teamName } = this.props;
    const inputName = event.currentTarget.name;
    const value = event.currentTarget.value;
    const username = nickname;
    const name = firstName.concat(lastName);
    const user = {
      email,
      name,
      teamName,
      username
    };
    const validation = validatePassword(value, user);
    const verification = validation.rawVerification || {};
    const feedback = verification.feedback || {};
    if (inputName === 'password' && value.length) {
      if (value.length < 8) {
        this.setState({
          passwordError: intl.formatMessage(Messages.passwordLengthError)
        });
      }
      if (validation.containsPushbot) {
        this.setState({
          passwordError: intl.formatMessage(Messages.passwordPushbotError)
        });
      }
      if (validation.containsCatalytic) {
        this.setState({
          passwordError: intl.formatMessage(Messages.passwordCatalyticError)
        });
      }
      if (validation.containsEmail) {
        this.setState({
          passwordError: intl.formatMessage(Messages.passwordEmailError)
        });
      }
      if (validation.containsFullName) {
        this.setState({
          passwordError: intl.formatMessage(Messages.passwordNameError)
        });
      }
      if (validation.containsUsername) {
        this.setState({
          passwordError: intl.formatMessage(Messages.passwordNicknameError)
        });
      }
      if (validation.containsTeamName) {
        this.setState({
          passwordError: intl.formatMessage(Messages.passwordTeamError)
        });
      }

      if (feedback.warning) {
        this.setState({
          passwordError: feedback.warning
        });
      }
      if (validation.score < 3 && value.length > 7) {
        this.setState({
          passwordError: intl.formatMessage(Messages.passwordComplexityError)
        });
      }

      if (
        passwordError &&
        value.length &&
        value.length > 7 &&
        !validation.containsPushbot &&
        !validation.containsCatalytic &&
        !validation.containsEmail &&
        !validation.containsFullName &&
        !validation.containsUsername &&
        !validation.containsUserID &&
        !validation.containsTeamName &&
        !feedback.warning &&
        validation.score > 2
      ) {
        this.setState({ passwordError: '' });
      }
    }
    if (inputName === 'nickname' && value.length) {
      const reserved = isReservedName(value);
      const isAlphanumericNickname = isAlphanumeric(value);
      const isFirstCharacterLetter = isFirstLetter(value[0]);
      if (reserved) {
        this.setState({
          nicknameError: `The nickname "${value}" is reserved.`
        });
      } else if (!isAlphanumericNickname && !nicknameError) {
        this.setState({
          nicknameError: intl.formatMessage(
            Messages.nicknameNonAlphaNumericError
          )
        });
      } else if (!isFirstCharacterLetter) {
        this.setState({
          nicknameError: intl.formatMessage(Messages.nicknameFirstLetterError)
        });
      }
      if (
        nicknameError &&
        isAlphanumericNickname &&
        isFirstCharacterLetter &&
        !reserved
      ) {
        this.setState({ nicknameError: '' });
      }
    }
    if (error || emailError || firstNameError || lastNameError) {
      this.setState({
        error: '',
        firstNameError: '',
        lastNameError: '',
        emailError: ''
      });
    }
    if (inputName === 'password' && value === '') {
      this.setState({ passwordError: '' });
    }
    if (inputName === 'nickname' && value === '') {
      this.setState({ nicknameError: '' });
    }

    this.setState({
      [inputName]: event.currentTarget.value
    });
  };

  handleSubmit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { history, intl } = this.props;
    const { disabled, email, firstName, lastName, token } = this.state;

    let { password, nickname } = this.state;

    if (disabled && !password) {
      password = uuid.v4();
    }

    // Replaces nickname/username with UUID, with underscores instead of hyphens and a leading lowercase letter to ensure username validity

    if (HIDE_NICKNAME) {
      nickname = 'a' + uuid.v4().replace(/-/g, '_');
    }

    // TODO: Move Join call to GQL layer: https://github.com/catalyticlabs/product/issues/5218
    return fetch('/api/invites/' + token, {
      headers: { 'Content-type': 'application/json' },
      method: 'POST',
      credentials: 'same-origin',
      body: JSON.stringify({
        email: email.toLowerCase(),
        fullName: `${firstName} ${lastName}`,
        username: nickname.toLowerCase(),
        password
      })
    })
      .then(res => {
        if (res.status === 200) {
          return fetch(`/api/users/${nickname.toLowerCase()}`).then(() =>
            history.push({
              pathname: '/'
            })
          );
        } else {
          res.json().then(res => {
            const error = res.message;
            if (error.includes('Invite') && error.includes('not found')) {
              history.push({ pathname: '/auth/join/invalid' });
            } else if (
              error.includes(email.toLowerCase()) &&
              error.includes('already exists')
            ) {
              this.setState({
                emailError: intl.formatMessage(Messages.emailErrorMessage, {
                  email
                })
              });
            } else if (
              error.includes(nickname.toLowerCase()) &&
              error.includes('already exists')
            ) {
              this.setState({
                nicknameError: `The nickname "${nickname}" is already being used.`
              });
            } else if (
              error.includes(nickname.toLowerCase()) &&
              nickname.length === 1
            ) {
              this.setState({
                nicknameError: intl.formatMessage(Messages.nicknameLengthError)
              });
            } else if (error.includes(nickname.toLowerCase())) {
              this.setState({
                nicknameError: error
              });
            } else if (error.includes('password')) {
              this.setState({
                passwordError: error
              });
            } else {
              this.setState({ error: error });
            }
          });
        }
      })
      .catch(e => {
        if (process.env.NODE_ENV !== 'test') {
          console.warn(intl.formatMessage(Messages.joinError), e);
        }
      });
  };

  render() {
    const { classes, fetched, intl, teamDisplayName } = this.props;
    const {
      disabled,
      email,
      emailError,
      firstNameError,
      lastNameError,
      nicknameError,
      password,
      passwordError
    } = this.state;

    return (
      <LoginSplitLayout fetched={fetched} hasTeamImage>
        <LoginHeadline
          headlineText={
            <FormattedMessage
              id="join.headline"
              defaultMessage="Join {teamDisplayName}"
              values={{ teamDisplayName }}
            />
          }
        />
        <form
          className={classes.form}
          data-testid="join-form"
          onSubmit={this.handleSubmit}
        >
          <Input
            className={classes.inputRoot}
            inputClassName={classes.input}
            error={firstNameError}
          >
            <TextInput
              required
              name={FIRST_NAME_KEY}
              onChange={this.handleChange}
              placeholder={intl.formatMessage(Messages.firstNamePlaceholder)}
              type="text"
            />
          </Input>
          <Input
            className={classes.inputRoot}
            inputClassName={classes.input}
            error={lastNameError}
          >
            <TextInput
              required
              name={LAST_NAME_KEY}
              onChange={this.handleChange}
              placeholder={intl.formatMessage(Messages.lastNamePlaceholder)}
              type="text"
            />
          </Input>
          {!HIDE_NICKNAME && (
            <Input
              className={classes.inputRoot}
              inputClassName={classes.input}
              error={nicknameError}
            >
              <TextInput
                required
                name={NICKNAME_KEY}
                onChange={this.handleChange}
                placeholder={intl.formatMessage(Messages.nicknamePlaceholder)}
                type="text"
              />
            </Input>
          )}
          {!disabled && (
            <Input
              className={classes.inputRoot}
              inputClassName={classes.input}
              error={emailError}
            >
              <TextInput
                name={EMAIL_KEY}
                inputMode="email"
                readOnly
                value={email}
                type="text"
              />
            </Input>
          )}
          {!disabled && (
            <Input
              className={classes.inputRoot}
              inputClassName={classes.input}
              error={passwordError}
            >
              <PasswordInput
                autoComplete="on"
                hasError={passwordError}
                required
                name={PASSWORD_KEY}
                onChange={this.handleChange}
                placeholder={intl.formatMessage(Messages.passwordPlaceholder)}
                type="password"
                value={password}
              />
            </Input>
          )}
          <LoginButton
            type="submit"
            value="submit"
            disabled={
              emailError ||
              firstNameError ||
              lastNameError ||
              nicknameError ||
              passwordError
            }
          >
            <FormattedMessage id="login.joinButton" defaultMessage="Join" />
          </LoginButton>
        </form>
      </LoginSplitLayout>
    );
  }
}
export default compose(
  withTeam,
  withRouter,
  injectIntl,
  injectSheet(Style)
)(Join);
